import { BrandPlanTactic, PostContentType } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostTypeCountInputProps, CROSSPOST_RELEVANT_TACTICS } from 'screens/Schedule/EditChannelsModal/Types'
import { ConnectAccount } from 'screens/Schedule/EditChannelsModal/ConnectAccount'
import { getTacticInfo, getProviderLabel } from 'components/TacticIntegrationList/utils'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import Styles from './Accounts.module.scss'
import { usePostTypeField } from 'screens/Schedule/EditChannelsModal/usePostTypeField'
import { useTacticWithCount } from 'screens/Schedule/EditChannelsModal/useTacticWithCount'
import { CrosspostAccounts } from 'screens/Schedule/EditChannelsModal/Crosspost/CrosspostAccounts'

interface AccountsWithToggleProps {
  tactics: BrandPlanTactic[]
  contentsFieldName: 'crosspost_contents' | 'video_crosspost_contents'
  postType: PostTypeCountInputProps['postType']
  contentType?: PostContentType
}

export const AccountsWithToggle = ({ tactics, contentsFieldName, postType, contentType }: AccountsWithToggleProps) => {
  const [field] = usePostTypeField({ name: contentsFieldName, postType, contentType })
  const {
    tactics: tacticValues,
    addTactic,
    removeTactic,
  } = useTacticWithCount(field.name, CROSSPOST_RELEVANT_TACTICS[contentsFieldName])

  // Map tactics to providers for fetching integrations
  const providersToFetch = Array.from(
    new Set(
      tactics
        .map((tactic) => getTacticInfo(tactic as any)?.integrationType)
        .filter((p): p is ExternalIntegrationEnum => p !== null && p !== undefined),
    ),
  )

  const { data } = useExternalIntegrations({
    select: (data) => ({
      external_integrations: data.external_integrations.filter((integration) =>
        providersToFetch.includes(integration.provider),
      ),
    }),
  })

  const integrations = data?.external_integrations || []

  return (
    <div className={Styles.wrapper}>
      {tactics.map((tactic) => {
        const tacticInfo = getTacticInfo(tactic as any)
        if (!tacticInfo) return null

        const provider = tacticInfo.integrationType
        const tacticIntegrations = integrations.filter((integration) => integration.provider === provider)

        if (tacticIntegrations.length > 0) {
          return (
            <CrosspostAccounts
              key={tactic}
              tactic={tactic}
              integrations={tacticIntegrations}
              contentType={contentType}
              crosspostField={contentsFieldName}
            />
          )
        }

        return (
          <div key={tactic} className={Styles.account} role="group" aria-label={getProviderLabel(provider)}>
            <ConnectAccount provider={provider} />
            <ToggleButton
              checked={tacticValues.includes(tactic)}
              name={`${field.name}.${tactic}`}
              size="sm-md"
              handleChange={(e) => {
                if (e.target.checked) {
                  addTactic(tactic)
                } else {
                  removeTactic(tactic)
                }
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
